import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Missing Supabase environment variables. Please check your .env file.'
  );
}

const COOKIE_MAX_AGE = 259200;

const cookieStorage = {
  getItem: (key) => {
    const match = document.cookie.match(new RegExp('(^| )' + encodeURIComponent(key) + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  },
  setItem: (key, value) => {
    document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Strict; Secure`;
  },
  removeItem: (key) => {
    document.cookie = `${encodeURIComponent(key)}=; max-age=0; path=/; SameSite=Strict; Secure`;
  },
};

export const clearAuthCookies = () => {
  const keys = document.cookie.split(';').map(c => c.trim().split('=')[0]).filter(k => k.startsWith('sb-'));
  keys.forEach(key => {
    document.cookie = `${key}=; max-age=0; path=/; SameSite=Strict; Secure`;
  });
};

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: cookieStorage,
  },
});

export const handleSupabaseError = (error) => {
  if (error) {
    console.error('Supabase error:', error.message);
    return {
      error: true,
      message: error.message || 'An unexpected error occurred',
    };
  }
  return { error: false };
};

export const auth = {
  getUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  getSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    return { session, error };
  },

  signUp: async (email, password, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: Object.keys(metadata).length > 0 ? { data: metadata } : undefined,
    });
    return { data, error };
  },

  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  resetPassword: async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  },

  updatePassword: async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { data, error };
  },

  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback);
  },
};

export const db = {
  profiles: {
    get: async (userId) => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      return { data, error };
    },

    update: async (userId, updates) => {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();
      return { data, error };
    },

    getDashboardStats: async (userId) => {
      const { data, error } = await supabase
        .from('user_dashboard_stats')
        .select('*')
        .eq('user_id', userId)
        .single();
      return { data, error };
    },
  },

  topups: {
    getAll: async (userId, options = {}) => {
      let query = supabase
        .from('topups')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (options.status) {
        query = query.eq('status', options.status);
      }

      if (options.limit) {
        query = query.limit(options.limit);
      }

      if (options.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data, error, count } = await query;
      return { data, error, count };
    },

    getById: async (topupId) => {
      const { data, error } = await supabase
        .from('topups')
        .select('*')
        .eq('id', topupId)
        .single();
      return { data, error };
    },

    create: async (topupData) => {
      const { data, error } = await supabase
        .from('topups')
        .insert(topupData)
        .select()
        .single();
      return { data, error };
    },

    updateStatus: async (topupId, status, metadata = {}) => {
      const { data, error } = await supabase
        .from('topups')
        .update({ status, metadata, updated_at: new Date().toISOString() })
        .eq('id', topupId)
        .select()
        .single();
      return { data, error };
    },
  },

  bandwidthPackages: {
    getAll: async (userId) => {
      const { data, error } = await supabase
        .from('bandwidth_packages')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      return { data, error };
    },

    getActive: async (userId, proxyType = null) => {
      let query = supabase
        .from('bandwidth_packages')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active');

      if (proxyType) {
        query = query.eq('proxy_type', proxyType);
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      return { data, error };
    },

    create: async (packageData) => {
      const { data, error } = await supabase
        .from('bandwidth_packages')
        .insert(packageData)
        .select()
        .single();
      return { data, error };
    },

    update: async (packageId, updates) => {
      const { data, error } = await supabase
        .from('bandwidth_packages')
        .update(updates)
        .eq('id', packageId)
        .select()
        .single();
      return { data, error };
    },

    delete: async (packageId) => {
      const { error } = await supabase
        .from('bandwidth_packages')
        .delete()
        .eq('id', packageId);
      return { error };
    },

    pause: async (packageId) => {
      const { data, error } = await supabase
        .from('bandwidth_packages')
        .update({ status: 'paused' })
        .eq('id', packageId)
        .select()
        .single();
      return { data, error };
    },

    resume: async (packageId) => {
      const { data, error } = await supabase
        .from('bandwidth_packages')
        .update({ status: 'active' })
        .eq('id', packageId)
        .select()
        .single();
      return { data, error };
    },
  },

  trafficLogs: {
    getByDate: async (userId, date) => {
      const { data, error } = await supabase
        .from('traffic_logs')
        .select('*')
        .eq('user_id', userId)
        .eq('date', date)
        .order('hour', { ascending: true });
      return { data, error };
    },

    getByDateRange: async (userId, startDate, endDate) => {
      const { data, error } = await supabase
        .from('traffic_logs')
        .select('*')
        .eq('user_id', userId)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true })
        .order('hour', { ascending: true });
      return { data, error };
    },

    getTotalByType: async (userId) => {
      const { data, error } = await supabase
        .from('traffic_logs')
        .select('proxy_type, data_used_gb')
        .eq('user_id', userId);

      if (error) return { data: null, error };

      const totals = data.reduce((acc, log) => {
        acc[log.proxy_type] = (acc[log.proxy_type] || 0) + parseFloat(log.data_used_gb);
        return acc;
      }, {});

      return { data: totals, error: null };
    },
  },

  pricingTiers: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('pricing_tiers')
        .select('*')
        .order('proxy_type')
        .order('min_gb');
      return { data, error };
    },

    getByType: async (proxyType) => {
      const { data, error } = await supabase
        .from('pricing_tiers')
        .select('*')
        .eq('proxy_type', proxyType)
        .eq('is_payg', false)
        .order('min_gb');
      return { data, error };
    },

    calculatePrice: async (proxyType, gbAmount) => {
      const { data: tiers, error } = await supabase
        .from('pricing_tiers')
        .select('*')
        .eq('proxy_type', proxyType)
        .eq('is_payg', false)
        .order('min_gb');

      if (error) return { price: null, pricePerGb: null, error };

      const tier = tiers.find(t =>
        gbAmount >= t.min_gb && (t.max_gb === null || gbAmount <= t.max_gb)
      );

      if (!tier) {
        const highestTier = tiers[tiers.length - 1];
        return {
          price: gbAmount * parseFloat(highestTier.price_per_gb),
          pricePerGb: parseFloat(highestTier.price_per_gb),
          error: null,
        };
      }

      return {
        price: gbAmount * parseFloat(tier.price_per_gb),
        pricePerGb: parseFloat(tier.price_per_gb),
        error: null,
      };
    },
  },

  proxySessions: {
    getActive: async (userId) => {
      const { data, error } = await supabase
        .from('proxy_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active');
      return { data, error };
    },

    create: async (sessionData) => {
      const { data, error } = await supabase
        .from('proxy_sessions')
        .insert(sessionData)
        .select()
        .single();
      return { data, error };
    },

    terminate: async (sessionId) => {
      const { data, error } = await supabase
        .from('proxy_sessions')
        .update({ status: 'terminated' })
        .eq('id', sessionId)
        .select()
        .single();
      return { data, error };
    },
  },

  purchaseHistory: {
    getByPackage: async (packageId) => {
      const { data, error } = await supabase
        .from('purchase_history')
        .select('*')
        .eq('package_id', packageId)
        .order('created_at', { ascending: false });
      return { data, error };
    },

    getByUser: async (userId, options = {}) => {
      let query = supabase
        .from('purchase_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;
      return { data, error };
    },

    create: async (historyData) => {
      const { data, error } = await supabase
        .from('purchase_history')
        .insert(historyData)
        .select()
        .single();
      return { data, error };
    },
  },

  referrals: {
    getByCode: async (referralCode) => {
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('referral_code', referralCode.toLowerCase())
        .order('created_at', { ascending: false });

      return { data: data || [], error };
    },

    checkCodeExists: async (referralCode) => {
      const { data, error } = await supabase
        .from('profiles')
        .select('referral_code')
        .eq('referral_code', referralCode.toLowerCase())
        .maybeSingle();
      return { exists: !!data, error };
    },

    createReferral: async (referralData) => {
      const { data, error } = await supabase
        .from('referrals')
        .insert(referralData)
        .select()
        .single();
      return { data, error };
    },

    updateBandwidth: async (referredUserId, bandwidthPurchased, bandwidthUsed) => {
      const { data, error } = await supabase
        .from('referrals')
        .update({
          bandwidth_purchased: bandwidthPurchased,
          bandwidth_used: bandwidthUsed,
        })
        .eq('referred_user_id', referredUserId)
        .select()
        .single();
      return { data, error };
    },

    getByReferredUser: async (userId) => {
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('referred_user_id', userId)
        .maybeSingle();
      return { data, error };
    },

    getProfileByReferralCode: async (referralCode) => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, referral_code')
        .eq('referral_code', referralCode.toLowerCase())
        .maybeSingle();
      return { data, error };
    },

    setReferredBy: async (userId, referrerId) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({ referred_by: referrerId })
        .eq('id', userId)
        .select()
        .single();
      return { data, error };
    },

    createReferralStat: async (referrerId, referredId) => {
      const { data, error } = await supabase
        .from('referral_stats')
        .insert({
          referrer_id: referrerId,
          referred_id: referredId,
        })
        .select()
        .single();
      return { data, error };
    },

    getReferredProfiles: async (referrerId) => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, created_at')
        .eq('referred_by', referrerId)
        .order('created_at', { ascending: false });
      return { data: data || [], error };
    },

    getStatsByReferrer: async (referrerId) => {
      const { data, error } = await supabase
        .from('referral_stats')
        .select('*')
        .eq('referrer_id', referrerId)
        .order('created_at', { ascending: false });
      return { data: data || [], error };
    },
  },

  tickets: {
    getAll: async (userId) => {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      return { data, error };
    },

    getById: async (ticketId, userId) => {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .eq('id', ticketId)
        .eq('user_id', userId)
        .single();
      return { data, error };
    },

    getMessages: async (ticketId) => {
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      return { data, error };
    },

    getOpenCount: async (userId) => {
      const { count, error } = await supabase
        .from('tickets')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'open');
      return { count: count || 0, error };
    },
  },

  notifications: {
    getAll: async (userId, options = {}) => {
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (options.unreadOnly) {
        query = query.eq('is_read', false);
      }

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;
      return { data, error };
    },

    getUnreadCount: async (userId) => {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);
      return { count: count || 0, error };
    },

    markAsRead: async (notificationId) => {
      const { data, error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .select()
        .single();
      return { data, error };
    },

    markAllAsRead: async (userId) => {
      const { data, error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('is_read', false)
        .select();
      return { data, error };
    },

    delete: async (notificationId) => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);
      return { error };
    },

    deleteAll: async (userId) => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId);
      return { error };
    },
  },
};

export const realtime = {
  subscribeToProfile: (userId, callback) => {
    return supabase
      .channel(`profile:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`,
        },
        callback
      )
      .subscribe();
  },

  subscribeToTopups: (userId, callback) => {
    return supabase
      .channel(`topups:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'topups',
          filter: `user_id=eq.${userId}`,
        },
        callback
      )
      .subscribe();
  },

  subscribeToBandwidth: (userId, callback) => {
    return supabase
      .channel(`bandwidth:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bandwidth_packages',
          filter: `user_id=eq.${userId}`,
        },
        callback
      )
      .subscribe();
  },

  subscribeToNotifications: (userId, callback) => {
    return supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        callback
      )
      .subscribe();
  },

  subscribeToTicketMessages: (ticketId, callback) => {
    return supabase
      .channel(`ticket_messages:${ticketId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_messages',
          filter: `ticket_id=eq.${ticketId}`,
        },
        callback
      )
      .subscribe();
  },

  subscribeToTickets: (userId, callback) => {
    return supabase
      .channel(`tickets:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tickets',
          filter: `user_id=eq.${userId}`,
        },
        callback
      )
      .subscribe();
  },

  unsubscribe: (channel) => {
    supabase.removeChannel(channel);
  },
};

export default supabase;
